import React, { Suspense } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Navbar } from "./components/Navbar";

// Eagerly loaded — always needed
import LandingPage from "./pages/LandingPage";

// Lazy-loaded — only fetched when the user navigates to that route
const ToolsPage = React.lazy(() => import("./pages/ToolsPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const BoundTimelinePage = React.lazy(() => import("./pages/BoundTimelinePage"));
const TimelinePage = React.lazy(() => import("./pages/TimelinePage"));
const FileRenamerPage = React.lazy(() => import("./pages/FileRenamerPage"));
const DiscoveryPage = React.lazy(() => import("./pages/DiscoveryPage"));
const ChangelogPage = React.lazy(() => import("./pages/ChangelogPage"));
const QCRunnerPage = React.lazy(() => import("./pages/QCRunnerPage"));
const ExhibitBuilderPage = React.lazy(() => import("./pages/ExhibitBuilderPage"));
const FamilyBinderPage = React.lazy(() => import("./pages/FamilyBinderPage"));

const KNOWN_PATHS = new Set([
  "/",
  "/tools",
  "/tools/timeline",
  "/tools/renamer",
  "/tools/discovery",
  "/tools/qc-runner",
  "/tools/all-in-one",
  "/tools/exhibit",
  "/tools/family-binder",
  "/settings",
  "/changelog",
  "/index.html",
]);

export function normalizePathname(pathname) {
  let next = String(pathname || "/");
  next = next.replace(/\/{2,}/g, "/");
  next = next.replace(/\/index\.html\/?$/i, "");
  if (!next.startsWith("/")) {
    next = `/${next}`;
  }
  if (next.length > 1) {
    next = next.replace(/\/+$/, "");
  }
  return next || "/";
}

function IndexHtmlRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasOAuthParams = params.has("code") || params.has("state") || params.has("error");
  const target = hasOAuthParams
    ? `/settings${location.search || ""}${location.hash || ""}`
    : `/${location.search || ""}${location.hash || ""}`;
  return <Navigate to={target} replace />;
}

function IndexPathNormalizer() {
  const location = useLocation();
  if (!/\/index\.html$/i.test(location.pathname)) {
    return null;
  }

  const params = new URLSearchParams(location.search);
  const hasOAuthParams = params.has("code") || params.has("state") || params.has("error");
  if (hasOAuthParams) {
    return <Navigate to={`/settings${location.search || ""}${location.hash || ""}`} replace />;
  }

  const normalizedPath = location.pathname.replace(/\/index\.html$/i, "") || "/";
  return <Navigate to={`${normalizedPath}${location.search || ""}${location.hash || ""}`} replace />;
}

function NotFoundRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasOAuthParams = params.has("code") || params.has("state") || params.has("error");
  if (hasOAuthParams) {
    return <Navigate to={`/settings${location.search || ""}${location.hash || ""}`} replace />;
  }

  const normalizedPath = normalizePathname(location.pathname);
  if (normalizedPath !== location.pathname && KNOWN_PATHS.has(normalizedPath)) {
    return <Navigate to={`${normalizedPath}${location.search || ""}${location.hash || ""}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function Lazy({ page: Page }) {
  return (
    <Suspense fallback={<div className="page-skeleton" />}>
      <Page />
    </Suspense>
  );
}

function ProtectedRoute({ children }) {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!isAuthenticated) {
    try { sessionStorage.setItem("pkec_redirect_after_login", location.pathname); } catch (_) {}
    return <Navigate to="/" replace />;
  }
  return children;
}

function OAuthCallbackRedirect() {
  const location = useLocation();
  if (location.pathname === "/settings") {
    return null;
  }
  const params = new URLSearchParams(location.search);
  const hasOAuthParams = params.has("code") || params.has("state") || params.has("error");
  if (!hasOAuthParams) {
    return null;
  }
  return <Navigate to={`/settings${location.search || ""}${location.hash || ""}`} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <IndexPathNormalizer />
          <OAuthCallbackRedirect />
          <div className="app-shell min-h-screen text-foreground transition-theme">
            <div className="relative z-10">
              <Navbar />
              <main className="page-stage">
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/tools" element={<Lazy page={ToolsPage} />} />
                  <Route path="/tools/bound-timeline" element={<ProtectedRoute><Lazy page={BoundTimelinePage} /></ProtectedRoute>} />
                  <Route path="/tools/timeline" element={<ProtectedRoute><Lazy page={TimelinePage} /></ProtectedRoute>} />
                  <Route path="/tools/renamer" element={<ProtectedRoute><Lazy page={FileRenamerPage} /></ProtectedRoute>} />
                  <Route path="/tools/discovery" element={<ProtectedRoute><Lazy page={DiscoveryPage} /></ProtectedRoute>} />
                  <Route path="/tools/qc-runner" element={<ProtectedRoute><Lazy page={QCRunnerPage} /></ProtectedRoute>} />
                  {/* Preserve the deprecated URL as an alias for the replacement workflow. */}
                  <Route path="/tools/all-in-one" element={<Navigate to="/tools/bound-timeline" replace />} />
                  <Route path="/tools/exhibit" element={<ProtectedRoute><Lazy page={ExhibitBuilderPage} /></ProtectedRoute>} />
                  <Route path="/tools/family-binder" element={<ProtectedRoute><Lazy page={FamilyBinderPage} /></ProtectedRoute>} />
                  <Route path="/settings" element={<Lazy page={SettingsPage} />} />
                  <Route path="/changelog" element={<Lazy page={ChangelogPage} />} />
                  <Route path="/index.html" element={<IndexHtmlRedirect />} />
                  <Route path="*" element={<NotFoundRedirect />} />
                </Routes>
              </main>
              <Toaster position="bottom-right" offset={20} style={{ zIndex: 100000 }} />
            </div>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
